import {
  Volume,
  Gain,
  ToneAudioNode,
  getDestination,
  start,
  now,
  getTransport,
  getContext,
  BaseContext,
} from 'tone';
import { gainToDb } from '@waveform-playlist/core';
import { ToneTrack, ToneTrackOptions, type ClipInfo } from './ToneTrack';
import { MidiToneTrack, MidiToneTrackOptions } from './MidiToneTrack';
import type { PlayableTrack } from './MidiToneTrack';
import { SoundFontToneTrack, SoundFontToneTrackOptions } from './SoundFontToneTrack';

export type EffectsFunction = (
  masterGainNode: Volume,
  destination: ToneAudioNode,
  isOffline: boolean
) => void | (() => void);

export interface TonePlayoutOptions {
  tracks?: ToneTrack[];
  masterGain?: number;
  effects?: EffectsFunction;
}

export class TonePlayout {
  private tracks: Map<string, PlayableTrack> = new Map();
  private masterVolume: Volume;
  private _masterTap: Gain;
  private isInitialized = false;
  private soloedTracks: Set<string> = new Set();
  private manualMuteState: Map<string, boolean> = new Map();
  private effectsCleanup?: () => void;
  private onPlaybackCompleteCallback?: () => void;
  private _completionEventId: number | null = null;
  private _loopHandler: (() => void) | null = null;
  private _loopEnabled = false;
  private _loopStart = 0;
  private _loopEnd = 0;
  private _masterChainNode: AudioNode | null = null;

  constructor(options: TonePlayoutOptions = {}) {
    this.masterVolume = new Volume(gainToDb(options.masterGain ?? 1));
    // Pass-through tap node for consumer connections (analyzers, recorders).
    // Shares the same standardized-audio-context as masterVolume.
    this._masterTap = new Gain(1);

    // Serial chain: masterVolume → tap → destination.
    // When effects are present: masterVolume → effects → tap → destination.
    // Consumers connect analyzers, effects, recorders to the tap node.
    if (options.effects) {
      const cleanup = options.effects(this.masterVolume, this._masterTap, false);
      if (cleanup) {
        this.effectsCleanup = cleanup;
      }
      this._masterTap.connect(getDestination());
    } else {
      this.masterVolume.chain(this._masterTap, getDestination());
    }

    if (options.tracks) {
      options.tracks.forEach((track) => {
        this.tracks.set(track.id, track);
        this.manualMuteState.set(track.id, track.muted);
      });
    }
  }

  private clearCompletionEvent(): void {
    if (this._completionEventId !== null) {
      try {
        getTransport().clear(this._completionEventId);
      } catch (err) {
        console.warn(
          '[waveform-playlist] Error clearing Transport completion event:' + ' ' + String(err)
        );
      }
      this._completionEventId = null;
    }
  }

  async init(): Promise<void> {
    if (this.isInitialized) return;

    await start();
    this.isInitialized = true;
  }

  addTrack(trackOptions: ToneTrackOptions): ToneTrack {
    // Ensure tracks connect to master volume instead of destination
    const optionsWithDestination = {
      ...trackOptions,
      destination: this.masterVolume,
    };
    const toneTrack = new ToneTrack(optionsWithDestination);
    this.tracks.set(toneTrack.id, toneTrack);
    this.manualMuteState.set(toneTrack.id, trackOptions.track.muted ?? false);
    if (trackOptions.track.soloed) {
      this.soloedTracks.add(toneTrack.id);
    }
    return toneTrack;
  }

  addMidiTrack(trackOptions: MidiToneTrackOptions): MidiToneTrack {
    const optionsWithDestination = {
      ...trackOptions,
      destination: this.masterVolume,
    };
    const midiTrack = new MidiToneTrack(optionsWithDestination);
    this.tracks.set(midiTrack.id, midiTrack);
    this.manualMuteState.set(midiTrack.id, trackOptions.track.muted ?? false);
    if (trackOptions.track.soloed) {
      this.soloedTracks.add(midiTrack.id);
    }
    return midiTrack;
  }

  addSoundFontTrack(trackOptions: SoundFontToneTrackOptions): SoundFontToneTrack {
    const optionsWithDestination = {
      ...trackOptions,
      destination: this.masterVolume,
    };
    const sfTrack = new SoundFontToneTrack(optionsWithDestination);
    this.tracks.set(sfTrack.id, sfTrack);
    this.manualMuteState.set(sfTrack.id, trackOptions.track.muted ?? false);
    if (trackOptions.track.soloed) {
      this.soloedTracks.add(sfTrack.id);
    }
    return sfTrack;
  }

  /**
   * Apply solo muting after all tracks have been added.
   * Call this after adding all tracks to ensure solo logic is applied correctly.
   */
  applyInitialSoloState(): void {
    this.updateSoloMuting();
  }

  removeTrack(trackId: string): void {
    const track = this.tracks.get(trackId);
    if (track) {
      track.dispose();
      this.tracks.delete(trackId);
      this.manualMuteState.delete(trackId);
      this.soloedTracks.delete(trackId);
    }
  }

  getTrack(trackId: string): PlayableTrack | undefined {
    return this.tracks.get(trackId);
  }

  getTrackIds(): string[] {
    return [...this.tracks.keys()];
  }

  /**
   * Replace clips on a track, preserving the track's audio graph.
   * Only works for ToneTrack (audio clips), not MidiToneTrack.
   */
  replaceTrackClips(trackId: string, newClips: ClipInfo[], newStartTime?: number): boolean {
    const track = this.tracks.get(trackId);
    if (!track || !('replaceClips' in track)) return false;
    (track as ToneTrack).replaceClips(newClips, newStartTime);
    return true;
  }

  /**
   * Start mid-clip sources for a specific track at the current Transport position.
   * Call after adding/updating a track during active playback so clips that span
   * the current position produce audio immediately.
   */
  resumeTrackMidPlayback(trackId: string): void {
    const track = this.tracks.get(trackId);
    if (!track) return;
    const transport = getTransport();
    if (transport.state !== 'started') return;
    const context = getContext();
    // Use audible position (transportOffset - lookAhead) so new sources
    // overlap with remaining buffered audio for a seamless transition
    const lookAhead = context.lookAhead ?? 0;
    const audibleOffset = Math.max(0, transport.seconds - lookAhead);
    track.startMidClipSources(audibleOffset, context.currentTime);
    // Fades were last prepared at play(), before this track was added or
    // swapped in — schedule its envelopes for the current cycle (no-op for
    // MIDI tracks, whose prepareFades is empty).
    track.prepareFades(context.currentTime, audibleOffset);
  }

  play(when?: number, offset?: number, duration?: number): void {
    if (!this.isInitialized) {
      throw new Error('[waveform-playlist] TonePlayout not initialized. Call init() first.');
    }

    const startTime = when ?? now();
    const transport = getTransport();

    this.clearCompletionEvent();

    const transportOffset = offset ?? 0;
    this.tracks.forEach((track) => {
      track.cancelFades();
      track.prepareFades(startTime, transportOffset);
    });

    // Schedule duration-limited stop via Transport
    if (duration !== undefined) {
      this._completionEventId = transport.scheduleOnce(() => {
        this._completionEventId = null;
        try {
          this.onPlaybackCompleteCallback?.();
        } catch (err) {
          console.warn(
            '[waveform-playlist] Error in playback completion callback:' + ' ' + String(err)
          );
        }
      }, transportOffset + duration);
    }

    // Start Transport — triggers schedule() callbacks for clips at/after offset
    try {
      // Stop all active native sources before restarting to prevent layered audio.
      // Native AudioBufferSourceNodes don't respond to Transport state changes.
      if (transport.state !== 'stopped') {
        transport.stop();
      }
      this.tracks.forEach((track) => track.stopAllSources());

      // Set loop boundaries BEFORE enabling loop. _processTick checks
      // `ticks >= _loopEnd` every tick; _loopEnd defaults to 0.
      transport.loopStart = this._loopStart;
      transport.loopEnd = this._loopEnd;
      transport.loop = this._loopEnabled;

      // Set schedule guard BEFORE transport.start(). Ghost ticks from stale
      // Clock._lastUpdate can fire schedule callbacks at past positions;
      // the guard suppresses callbacks for clips before the play offset
      // (those are handled by startMidClipSources below).
      this.tracks.forEach((track) => track.setScheduleGuardOffset(transportOffset));

      if (offset !== undefined) {
        transport.start(startTime, offset);
      } else {
        transport.start(startTime);
      }

      // Advance Clock._lastUpdate past the stop/start boundary.
      //
      // After stop/start cycles, _lastUpdate is stale (set by the previous
      // context tick, before our stop/start). The next Clock._loop() processes
      // ticks from [_lastUpdate, now()]. In the gap [_lastUpdate, startTime),
      // the TickSource is still "started" from the PREVIOUS play cycle with
      // its old tick offset. Those accumulated ticks can exceed _loopEnd,
      // causing _processTick to wrap immediately to loopStart.
      //
      // By advancing _lastUpdate to startTime, we skip the stale range.
      // The next Clock._loop() only processes [startTime, now()] — ticks
      // from the current play cycle with the correct offset.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Tone.js private internal, see CLAUDE.md ghost tick fix
      (transport as any)._clock._lastUpdate = startTime;

      // Start sources for clips that span the current Transport position.
      // Transport.schedule() only fires for clips at/after the offset;
      // clips whose start time is before the offset need manual creation.
      this.tracks.forEach((track) => {
        track.startMidClipSources(transportOffset, startTime);
      });
    } catch (err) {
      // Clean up scheduled events since Transport failed to start
      this.clearCompletionEvent();
      this.tracks.forEach((track) => track.cancelFades());
      console.warn(
        '[waveform-playlist] Transport.start() failed. Audio playback could not begin.' +
          ' ' +
          String(err)
      );
      throw err;
    }
  }

  pause(): void {
    const transport = getTransport();
    try {
      transport.pause();
    } catch (err) {
      console.warn('[waveform-playlist] Transport.pause() failed:' + ' ' + String(err));
    }
    // Native AudioBufferSourceNodes ignore Transport state changes —
    // they must be explicitly stopped.
    this.tracks.forEach((track) => track.stopAllSources());
    this.tracks.forEach((track) => track.cancelFades());
    this.clearCompletionEvent();
  }

  stop(): void {
    const transport = getTransport();
    try {
      transport.stop();
    } catch (err) {
      console.warn('[waveform-playlist] Transport.stop() failed:' + ' ' + String(err));
    }
    // Remove loop handler before stopping sources. Prevents any deferred
    // loop event from creating new sources via startMidClipSources() after
    // stopAllSources() runs. Defense-in-depth — setLoop(false) should
    // already have removed it, but stop() must be self-contained.
    if (this._loopHandler) {
      try {
        transport.off('loop', this._loopHandler);
      } catch (err) {
        console.warn('[waveform-playlist] Error removing loop handler:' + ' ' + String(err));
      }
      this._loopHandler = null;
    }
    // Stop all native sources explicitly
    this.tracks.forEach((track) => {
      try {
        track.stopAllSources();
      } catch (err) {
        console.warn(
          `[waveform-playlist] Error stopping sources for track "${track.id}":` + ' ' + String(err)
        );
      }
    });
    this.tracks.forEach((track) => {
      try {
        track.cancelFades();
      } catch (err) {
        console.warn(
          `[waveform-playlist] Error canceling fades for track "${track.id}":` + ' ' + String(err)
        );
      }
    });
    this.clearCompletionEvent();
  }

  setMasterGain(gain: number): void {
    this.masterVolume.volume.value = gainToDb(gain);
  }

  /** The master output tap node. In the signal chain: masterVolume → tap → destination.
   *  Connect analyzers/effects/recorders here — parallel or serial.
   *  The tap's native GainNode is on the same standardized-audio-context as adapter.audioContext. */
  get masterOutputNode(): GainNode {
    return this._masterTap.input;
  }

  /**
   * Native GainNode behind masterVolume.input — the master-bus junction that
   * per-track effect chains reconnect into (pre master volume). Distinct from
   * masterOutputNode (the post-volume tap for analyzers).
   */
  get masterBusInputNode(): GainNode {
    return (this.masterVolume.input as unknown as Gain).input;
  }

  /** Insert an external chain on a track: muteGain → node (caller wires node onward). */
  connectTrackOutput(trackId: string, node: AudioNode): void {
    const track = this.tracks.get(trackId);
    if (!track) {
      throw new Error('[waveform-playlist] connectTrackOutput: unknown track "' + trackId + '"');
    }
    if (!(track instanceof ToneTrack)) {
      throw new Error(
        '[waveform-playlist] connectTrackOutput: per-track effects chains are not supported ' +
          'for MIDI tracks on the Tone adapter (track "' +
          trackId +
          '")'
      );
    }
    track.connectEffects(node);
  }

  /** Restore a track's direct connection. No-op for unknown or MIDI tracks. */
  disconnectTrackOutput(trackId: string): void {
    const track = this.tracks.get(trackId);
    if (track instanceof ToneTrack) {
      track.disconnectEffects();
    }
  }

  /**
   * Insert an external master chain after the tap: masterVolume → [closure
   * effects] → tap → node (caller wires node.output → ctx.destination).
   */
  connectMasterOutput(node: AudioNode): void {
    // Guard the disconnect (as disconnectMasterOutput does) — a consumer may
    // have disposed the old chain node externally; the rewire must proceed.
    try {
      if (this._masterChainNode) {
        this._masterTap.disconnect(this._masterChainNode);
      } else {
        this._masterTap.disconnect(getDestination());
      }
    } catch (err) {
      console.warn('[waveform-playlist] connectMasterOutput disconnect: ' + String(err));
    }
    this._masterTap.connect(node);
    this._masterChainNode = node;
  }

  /** Restore tap → destination. Safe when no master chain is connected. */
  disconnectMasterOutput(): void {
    if (!this._masterChainNode) return;
    try {
      this._masterTap.disconnect(this._masterChainNode);
    } catch (err) {
      console.warn(
        '[waveform-playlist] disconnectMasterOutput: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
    this._masterTap.connect(getDestination());
    this._masterChainNode = null;
  }

  setSolo(trackId: string, soloed: boolean): void {
    const track = this.tracks.get(trackId);
    if (track) {
      track.setSolo(soloed);
      if (soloed) {
        this.soloedTracks.add(trackId);
      } else {
        this.soloedTracks.delete(trackId);
      }
      this.updateSoloMuting();
    }
  }

  private updateSoloMuting(): void {
    const hasSoloedTracks = this.soloedTracks.size > 0;

    this.tracks.forEach((track, id) => {
      if (hasSoloedTracks) {
        if (!this.soloedTracks.has(id)) {
          track.setMute(true);
        } else {
          const manuallyMuted = this.manualMuteState.get(id) ?? false;
          track.setMute(manuallyMuted);
        }
      } else {
        const manuallyMuted = this.manualMuteState.get(id) ?? false;
        track.setMute(manuallyMuted);
      }
    });
  }

  setMute(trackId: string, muted: boolean): void {
    const track = this.tracks.get(trackId);
    if (track) {
      this.manualMuteState.set(trackId, muted);
      // Route through solo-aware muting: while any track is soloed, a
      // non-soloed track must stay silent regardless of its manual mute —
      // the manual state is remembered and applies when solo clears.
      this.updateSoloMuting();
    }
  }

  setLoop(enabled: boolean, loopStart: number, loopEnd: number): void {
    // Update cached state first — play() uses these values to configure
    // the Transport on next start, so they must reflect the caller's intent
    // regardless of whether Transport property setting succeeds.
    this._loopEnabled = enabled;
    this._loopStart = loopStart;
    this._loopEnd = loopEnd;

    const transport = getTransport();
    try {
      // Set boundaries BEFORE enabling loop. Tone.js's _processTick checks
      // `ticks >= _loopEnd` on every tick. If we set transport.loop = true
      // first, a tick could fire before loopEnd is updated, seeing the stale
      // _loopEnd value (0 from Transport default) and wrapping immediately.
      transport.loopStart = loopStart;
      transport.loopEnd = loopEnd;
      transport.loop = enabled;
    } catch (err) {
      console.warn('[waveform-playlist] Error configuring Transport loop:' + ' ' + String(err));
      return;
    }

    if (enabled && !this._loopHandler) {
      this._loopHandler = () => {
        // On loop boundary: stop old sources, re-schedule fades, start mid-clip sources.
        // Event ordering in Transport's tick processing (Tone.js 15.x _processTick):
        //   loopEnd → ticks reset → loopStart → loop → forEachAtTime(ticks)
        // Our loop handler fires BEFORE schedule callbacks, so:
        // 1. stopAllSources + cancelFades — clean slate
        // 2. startMidClipSources — for clips spanning loopStart boundary
        // 3. prepareFades — fresh fade envelopes
        // Then Transport fires schedule callbacks for clips at/after loopStart.
        const currentTime = now();
        this.tracks.forEach((track) => {
          try {
            track.stopAllSources();
            track.cancelFades();
            track.setScheduleGuardOffset(this._loopStart);
            track.startMidClipSources(this._loopStart, currentTime);
            track.prepareFades(currentTime, this._loopStart);
          } catch (err) {
            console.warn(
              `[waveform-playlist] Error re-scheduling track "${track.id}" on loop:` +
                ' ' +
                String(err)
            );
          }
        });
      };
      transport.on('loop', this._loopHandler);
    } else if (!enabled && this._loopHandler) {
      transport.off('loop', this._loopHandler);
      this._loopHandler = null;
    }
  }

  getCurrentTime(): number {
    return getTransport().seconds;
  }

  seekTo(time: number): void {
    getTransport().seconds = time;
  }

  dispose(): void {
    // Stop Transport and all active sources before disposing.
    // Without this, the global Transport singleton keeps firing scheduled
    // callbacks and native AudioBufferSourceNodes continue playing through
    // the shared AudioContext (e.g., during Docusaurus client-side navigation).
    try {
      this.stop();
    } catch (err) {
      console.warn(
        '[waveform-playlist] Error stopping Transport during dispose:' + ' ' + String(err)
      );
    }

    this.tracks.forEach((track) => {
      try {
        track.dispose();
      } catch (err) {
        console.warn(
          `[waveform-playlist] Error disposing track "${track.id}":` + ' ' + String(err)
        );
      }
    });
    this.tracks.clear();

    if (this.effectsCleanup) {
      try {
        this.effectsCleanup();
      } catch (err) {
        console.warn(
          '[waveform-playlist] Error during master effects cleanup:' + ' ' + String(err)
        );
      }
    }

    try {
      this._masterTap.dispose();
    } catch (err) {
      console.warn('[waveform-playlist] Error disposing master tap: ' + String(err));
    }

    try {
      this.masterVolume.dispose();
    } catch (err) {
      console.warn('[waveform-playlist] Error disposing master volume: ' + String(err));
    }
  }

  get context(): BaseContext {
    return getContext();
  }

  get sampleRate(): number {
    return getContext().sampleRate;
  }

  setOnPlaybackComplete(callback: () => void): void {
    this.onPlaybackCompleteCallback = callback;
  }
}
